import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import {
  defaultOptions,
  checkStatus,
  parseJSON,
  getLocationOrigin,
  postMethod,
  multipartJsonHeader
} from '../../../../services/fetchTools';

var PURPLE = "#7c3aed";
var FF = "'DM Sans','Segoe UI',system-ui,sans-serif";

var EVENT_META = {
  "Goal": { emoji: "⚽", color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
  "Self goal": { emoji: "🔙", color: "#dc2626", bg: "#fee2e2", border: "#fca5a5" },
  "Yellow card": { emoji: "🟡", color: "#ca8a04", bg: "#fef9c3", border: "#fde047" },
  "Red card": { emoji: "🟥", color: "#dc2626", bg: "#fee2e2", border: "#fca5a5" },
  "Assist": { emoji: "🎯", color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
};
function getEventMeta(name) {
  return EVENT_META[name] || { emoji: "📌", color: "#6b7280", bg: "#f3f4f6", border: "#d1d5db" };
}

function getEventIcon(name) {
  switch (name) {
    case "Goal": return require("../../../../img/goal.png");
    case "Self goal": return require("../../../../img/self.png");
    case "Yellow card": return require("../../../../img/yellow.png");
    case "Red card": return require("../../../../img/red.png");
    default: return require("../../../../img/assist.png");
  }
}

function XIcon(props) {
  var s = props.size || 14;
  var c = props.color || "#6b7280";
  return (
    <svg width={s} height={s} viewBox="0 0 14 14" fill="none" style={{ display: "block" }}>
      <path d="M2 2l10 10M12 2L2 12" stroke={c} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function TeamAvatar(props) {
  var team = props.team;
  var size = props.size || 72;
  var src;
  if (team.logo) {
    src = team.logo.includes("http") ? team.logo : "https://api.sporthood.in" + team.logo;
  } else {
    src = props.createImageFromInitials(500, team.name, props.getRandomColor());
  }
  return (
    <img src={src} alt={team.name} style={{
      width: size, height: size, borderRadius: "50%", objectFit: "cover",
      border: "3px solid #e5e7eb", boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
      background: "#f3f4f6", display: "block",
    }} />
  );
}

function EventHistoryPill(props) {
  var info = props.info;
  var onDelete = props.onDelete;
  var name = info.game_event ? info.game_event.name : "";
  var meta = getEventMeta(name);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: meta.bg, border: "1px solid " + meta.border,
      borderRadius: 24, padding: "5px 8px 5px 10px", marginBottom: 6,
    }}>
      <img src={getEventIcon(name)} alt="" style={{ width: 20, height: 20, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {info.player ? info.player.name : "\u2014"}
        </div>
        {info.event_time && (
          <div style={{ fontSize: 11, color: meta.color, fontWeight: 600, fontFamily: FF }}>{info.event_time}&apos;</div>
        )}
      </div>
      <button type="button" onClick={function () { onDelete(info.id); }} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", color: meta.color, display: "flex", alignItems: "center", borderRadius: 4, flexShrink: 0 }} title="Remove">
        <XIcon size={11} color={meta.color} />
      </button>
    </div>
  );
}

/**
 * AddPlayerInline
 *
 * Two-step flow matching AddTournamentTeamMember:
 *   1. Search existing customers via /eagleeye/search/
 *      → pick one  → POST /eagleeye/add_tournament_member/
 *   2. Or create a new customer via /eagleeye/add_new_customer_tournament/
 *      → on success → POST /eagleeye/add_tournament_member/
 *
 * On completion calls props.onAdded({ id, name }) which the parent appends
 * to extraPlayers and auto-selects in the dropdown.
 *
 * Props:
 *   meta              {object}
 *   teamId            {string|number}  — tournament_team record id
 *   tournamentId      {string|number}
 *   competitionId     {string|number}
 *   getLocationOrigin {func}
 *   onAdded           {func}  (player) => void
 *   onCancel          {func}
 */
class AddPlayerInline extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: "",
      searchLoading: false,
      searchResults: [],
      searched: false,
      showNewForm: false,
      newName: "",
      newPhone: "",
      saving: false,
      errors: {},
      serverError: "",
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.handlePickResult = this.handlePickResult.bind(this);
    this.handleSaveMember = this.handleSaveMember.bind(this);
    this.handleSaveNew = this.handleSaveNew.bind(this);
  }

  origin() {
    return this.props.getLocationOrigin ? this.props.getLocationOrigin() : "";
  }

  // Step 1a: search existing customers
  handleSearch() {
    var term = this.state.searchTerm.trim();
    if (!term || term.length < 3) {
      this.setState({ errors: { searchTerm: "Enter at least 3 characters" } });
      return;
    }
    this.setState({ searchLoading: true, searched: false, searchResults: [], errors: {}, serverError: "" });
    var self = this;
    const url = `${getLocationOrigin()}/eagleeye/search/`;
    const options = { ...defaultOptions };
    options['method'] = 'post';
    options['headers'] = { 'content-type': 'application/json' };
    options['body'] = JSON.stringify({ search: term, competition_id: self.props.competitionId });
    fetch(url, options)
      .then(checkStatus).then(parseJSON)
      .then(function (data) {
        if (data.Message) {
          self.setState({ searchLoading: false, searched: true, searchResults: [], serverError: data.Message });
          return;
        }
        self.setState({
          searchLoading: false,
          searched: true,
          searchResults: Array.isArray(data.results) ? data.results : [],
        });
      })
      .catch(function (err) {
        self.setState({ searchLoading: false, serverError: err.message || "Search failed" });
      });
  }

  // Final step: add the resolved customer to the tournament team, then
  // re-fetch the team's member list to obtain the integer TournamentTeamMember id.
  // (customer.id is a UUID — this cannot be used as player_id in match events,
  //  which requires the integer TournamentTeamMember PK.)
  handleSaveMember(customer) {
    this.setState({ saving: true, serverError: "" });
    var self = this;
    const url = `${getLocationOrigin()}/eagleeye/add_tournament_member/`;
    const options = { ...defaultOptions };
    options['method'] = 'post';
    options['headers'] = { 'content-type': 'application/json' };
    options['body'] = JSON.stringify({
      customer_list: [{ id: customer.id, name: customer.name, photo: customer.photo || "" }],
      tournament_team: self.props.teamId,
      amount: 0,
    });
    fetch(url, options)
      .then(checkStatus).then(parseJSON)
      .then(function (addData) {
        // Some API versions return the created member(s) directly — try those first.
        var directMember = (addData.members && addData.members[0])
          || addData.member
          || (addData.data && addData.data[0])
          || null;
        // Accept only if it looks like an integer id (not a UUID).
        if (directMember && directMember.id && !/[a-f0-9]{8}-/.test(String(directMember.id))) {
          self.setState({ saving: false });
          self.props.onAdded({ id: directMember.id, name: customer.name });
          return;
        }

        // Fallback: re-fetch this specific team's player list to get the integer
        // TournamentTeamMember PK the backend assigned.
        // Uses /eagleeye/get_competition_team?id=<teamId> — same endpoint as
        // editTournamentTeam, requires only teamId (no competition_id needed).
        var teamUrl = `${getLocationOrigin()}/eagleeye/get_competition_team?id=${self.props.teamId}`;
        var teamOptions = { ...defaultOptions };
        return fetch(teamUrl, teamOptions)
          .then(checkStatus).then(parseJSON)
          .then(function (teamData) {
            var players = (teamData.teamlist && teamData.teamlist.players) || [];
            // Match by name — pick the last occurrence in case of duplicates
            // (the most-recently added record will be at the end).
            var matches = players.filter(function (p) { return p.name === customer.name; });
            var found = matches.length ? matches[matches.length - 1] : null;
            self.setState({ saving: false });
            self.props.onAdded({ id: found ? found.id : customer.id, name: customer.name });
          });
      })
      .catch(function (err) {
        self.setState({ saving: false, serverError: err.message || "Failed to add member" });
      });
  }

  // Step 1b: pick from search results → save member
  handlePickResult(result) {
    this.handleSaveMember(result);
  }

  // Step 2: create brand-new customer → save member
  handleSaveNew() {
    var errors = {};
    var name = this.state.newName.trim();
    var phone = this.state.newPhone.trim();
    if (!name) errors.newName = "Name is required";
    // Phone is optional — backend handles missing numbers gracefully.
    if (Object.keys(errors).length) { this.setState({ errors: errors }); return; }

    this.setState({ saving: true, errors: {}, serverError: "" });
    var self = this;
    const url = `${getLocationOrigin()}/eagleeye/add_new_customer_tournament/`;
    const options = { ...defaultOptions };
    options['method'] = 'post';
    options['headers'] = { 'content-type': 'application/json' };
    options['body'] = JSON.stringify({
      customer_name: name,
      customer_mobile: phone,
      tournament_id: self.props.tournamentId,
      from_scoreboard: !phone,
    });
    fetch(url, options)
      .then(checkStatus).then(parseJSON)
      .then(function (data) {
        if (!data.success) {
          self.setState({ saving: false, serverError: data.message || "Failed to create player" });
          return;
        }
        self.handleSaveMember(data.customer);
      })
      .catch(function (err) {
        self.setState({ saving: false, serverError: err.message || "Failed to create player" });
      });
  }

  render() {
    var meta = this.props.meta;
    var s = this.state;
    var disabled = s.searchLoading || s.saving;
    var self = this;

    var inputSt = function (field) {
      return {
        width: "100%", boxSizing: "border-box", borderRadius: 8,
        border: "1.5px solid " + (s.errors[field] ? "#fca5a5" : "#d1d5db"),
        padding: "8px 10px", fontSize: 13, fontFamily: FF,
        outline: "none", background: "#fff",
      };
    };

    return (
      <div style={{ marginTop: 8, padding: "12px 14px", background: "#f9fafb", border: "1.5px dashed " + meta.border, borderRadius: 10 }}>

        <div style={{ fontSize: 11, fontWeight: 700, color: meta.color, marginBottom: 10, fontFamily: FF }}>
          👤 Add player not in list
        </div>

        {/* Server / API error */}
        {s.serverError && (
          <div style={{ fontSize: 12, color: "#dc2626", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: "7px 10px", marginBottom: 8, fontFamily: FF }}>
            {s.serverError}
          </div>
        )}

        {/* ── Search view ── */}
        {!s.showNewForm && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 3, fontFamily: FF }}>
              Search by name / phone
            </label>
            <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
              <input
                type="text"
                placeholder="min 3 characters…"
                value={s.searchTerm}
                onChange={function (e) { self.setState({ searchTerm: e.target.value, errors: {}, serverError: "" }); }}
                onKeyDown={function (e) { if (e.key === "Enter") self.handleSearch(); if (e.key === "Escape") self.props.onCancel(); }}
                style={Object.assign({}, inputSt("searchTerm"), { flex: 1 })}
                disabled={disabled}
                autoFocus
              />
              <button type="button" onClick={self.handleSearch} disabled={disabled}
                style={{ background: meta.color, color: "#fff", border: "none", borderRadius: 8, padding: "0 12px", fontWeight: 700, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.7 : 1, fontFamily: FF, flexShrink: 0 }}>
                {s.searchLoading ? "…" : "🔍"}
              </button>
            </div>

            {s.errors.searchTerm && (
              <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 6, fontFamily: FF }}>{s.errors.searchTerm}</div>
            )}

            {/* Search results */}
            {s.searched && (
              <div style={{ marginBottom: 8 }}>
                {s.searchResults.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {s.searchResults.map(function (x) {
                      return (
                        <button key={x.id} type="button"
                          onClick={function () { self.handlePickResult(x); }}
                          disabled={disabled}
                          style={{ textAlign: "left", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 10px", cursor: disabled ? "not-allowed" : "pointer", fontSize: 12, fontFamily: FF, opacity: disabled ? 0.6 : 1 }}>
                          <div style={{ fontWeight: 600, color: "#111827" }}>{x.name}</div>
                          <div style={{ color: "#6b7280", fontSize: 11, marginTop: 1 }}>{x.mobile_no}</div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#6b7280", fontFamily: FF }}>No results found.</div>
                )}
              </div>
            )}

            {/* Switch to create-new form */}
            <button type="button"
              onClick={function () { self.setState({ showNewForm: true, errors: {}, serverError: "" }); }}
              style={{ background: "none", border: "none", padding: "4px 0 2px", fontSize: 11, color: meta.color, fontWeight: 700, cursor: "pointer", fontFamily: FF }}>
              + Create new player
            </button>
          </div>
        )}

        {/* ── Create new player form ── */}
        {s.showNewForm && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 3, fontFamily: FF }}>
                Full name <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Rahul Menon"
                value={s.newName}
                onChange={function (e) { self.setState({ newName: e.target.value, errors: Object.assign({}, self.state.errors, { newName: "" }) }); }}
                style={inputSt("newName")}
                disabled={disabled}
                autoFocus
              />
              {s.errors.newName && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3, fontFamily: FF }}>{s.errors.newName}</div>}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 3, fontFamily: FF }}>
                Phone number <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="tel"
                placeholder="e.g. 9876543210"
                value={s.newPhone}
                onChange={function (e) { self.setState({ newPhone: e.target.value, errors: Object.assign({}, self.state.errors, { newPhone: "" }) }); }}
                style={inputSt("newPhone")}
                disabled={disabled}
              />
              {s.errors.newPhone && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3, fontFamily: FF }}>{s.errors.newPhone}</div>}
            </div>

            <button type="button"
              onClick={function () { self.setState({ showNewForm: false, errors: {}, serverError: "" }); }}
              style={{ background: "none", border: "none", padding: "0 0 6px", fontSize: 11, color: "#6b7280", cursor: "pointer", fontFamily: FF }}>
              ← Back to search
            </button>
          </div>
        )}

        {/* ── Footer buttons ── */}
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          {s.showNewForm && (
            <button type="button" onClick={self.handleSaveNew} disabled={disabled}
              style={{ flex: 1, background: meta.color, color: "#fff", border: "none", borderRadius: 7, padding: "8px 0", fontWeight: 700, fontSize: 12, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.7 : 1, fontFamily: FF }}>
              {s.saving ? "⏳ Saving…" : "✔ Save player"}
            </button>
          )}
          <button type="button" onClick={this.props.onCancel} disabled={s.saving}
            style={{ background: "#f3f4f6", color: "#374151", border: "1.5px solid #e5e7eb", borderRadius: 7, padding: "8px 14px", fontWeight: 600, fontSize: 12, cursor: s.saving ? "not-allowed" : "pointer", fontFamily: FF }}>
            Cancel
          </button>
        </div>

      </div>
    );
  }
}

/**
 * EventForm
 *
 * Props:
 *   dir, selectedEvent, scoreObj, teamMatch
 *   extraPlayers, showAddForm
 *   onShowAddForm, onHideAddForm, onPlayerSaved
 *   onChangePlayer, onChangeEventTime, onUpdateFootballScore
 *   onDismiss, getLocationOrigin, competitionId
 */
class EventForm extends PureComponent {
  render() {
    var p = this.props;
    var dir = p.dir;
    var selectedEvent = p.selectedEvent;
    var scoreObj = p.scoreObj || {};
    var teamMatch = p.teamMatch;
    var extraPlayers = p.extraPlayers || [];
    var showAddForm = p.showAddForm || false;

    if (!selectedEvent) return null;

    var meta = getEventMeta(selectedEvent.event);
    var allPlayers = (teamMatch && teamMatch.players ? teamMatch.players : []).concat(extraPlayers);

    var inputSt = {
      width: "100%", boxSizing: "border-box", borderRadius: 8,
      border: "1.5px solid #d1d5db", padding: "8px 10px",
      fontSize: 13, fontFamily: FF, outline: "none", background: "#fff",
    };

    return (
      <div style={{
        background: meta.bg, border: "1px solid " + meta.border,
        borderRadius: 12, padding: "12px 14px", marginTop: 8, position: "relative",
      }}>
        {p.onDismiss && (
          <button type="button" onClick={p.onDismiss} style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }} title="Close">
            <XIcon size={12} color={meta.color} />
          </button>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10, display: "flex", alignItems: "center", gap: 6, fontFamily: FF }}>
          <span style={{ fontSize: 15 }}>{meta.emoji}</span> {selectedEvent.event}
        </div>

        {/* Player select */}
        <div style={{ marginBottom: 4 }}>
          <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3, fontWeight: 600, fontFamily: FF }}>Player</label>
          <select
            value={scoreObj["player_id"] || ""}
            onChange={function (e) { p.onChangePlayer(e, dir); }}
            style={inputSt}
          >
            <option value="">Select player…</option>
            {allPlayers.map(function (pl, i) {
              return <option key={pl.id || i} value={pl.id}>{pl.name}</option>;
            })}
          </select>
        </div>

        {/* Toggle add-player form */}
        {!showAddForm && (
          <button type="button" onClick={p.onShowAddForm}
            style={{ background: "none", border: "none", padding: "2px 0 8px", fontSize: 11, color: meta.color, fontWeight: 700, cursor: "pointer", fontFamily: FF, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add player not in list
          </button>
        )}

        {/* Inline add-player form */}
        {showAddForm && (
          <AddPlayerInline
            meta={meta}
            teamId={teamMatch ? teamMatch.id : ""}
            tournamentId={teamMatch ? teamMatch.tournament : ""}
            competitionId={p.competitionId}
            getLocationOrigin={p.getLocationOrigin}
            onAdded={p.onPlayerSaved}
            onCancel={p.onHideAddForm}
          />
        )}

        {/* Minute */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 3, fontWeight: 600, fontFamily: FF }}>Minute</label>
          <input
            type="number" min="0" max="120" placeholder="e.g. 45"
            value={scoreObj["event_time"] || ""}
            onChange={function (e) { p.onChangeEventTime(e, dir); }}
            style={inputSt}
          />
        </div>

        <button type="button"
          style={{ width: "100%", background: PURPLE, color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: FF }}
          onClick={function () { p.onUpdateFootballScore(dir); }}>
          {meta.emoji} Log {selectedEvent.event}
        </button>
      </div>
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ScoreBoardMobile
// ─────────────────────────────────────────────────────────────────────────────
export class ScoreBoardMobile extends PureComponent {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    eventActionMatch: PropTypes.object,
    team_list: PropTypes.array,
    game_events: PropTypes.array,
    footballMatchLeftTeamScore: PropTypes.object,
    footballMatchRightTeamScore: PropTypes.object,
    leftOpen: PropTypes.bool,
    rightOpen: PropTypes.bool,
    submit_button_pressed: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onHandleModel: PropTypes.func.isRequired,
    onChangePlayer: PropTypes.func.isRequired,
    onChangeEventTime: PropTypes.func.isRequired,
    onUpdateFootballScore: PropTypes.func.isRequired,
    onDeleteEventHistory: PropTypes.func.isRequired,
    onSaveZeroZero: PropTypes.func.isRequired,
    createImageFromInitials: PropTypes.func.isRequired,
    getRandomColor: PropTypes.func.isRequired,
    getLocationOrigin: PropTypes.func,
    competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onPlayerAdded: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      leftExtraPlayers: [],
      rightExtraPlayers: [],
      leftShowAddForm: false,
      rightShowAddForm: false,
    };
    this.handleEventPill = this.handleEventPill.bind(this);
    this.handlePlayerSaved = this.handlePlayerSaved.bind(this);
  }

  handleEventPill(event, dir) {
    this.props.onHandleModel({ target: { value: String(event.id) } }, dir);
  }

  handlePlayerSaved(dir, player) {
    var extraKey = dir === "left" ? "leftExtraPlayers" : "rightExtraPlayers";
    var formKey = dir === "left" ? "leftShowAddForm" : "rightShowAddForm";
    this.setState(function (prev) {
      var update = {};
      update[extraKey] = prev[extraKey].concat(player);
      update[formKey] = false;
      return update;
    });
    // Auto-select the newly added player in the dropdown
    this.props.onChangePlayer({ target: { value: String(player.id) } }, dir);
    if (this.props.onPlayerAdded) this.props.onPlayerAdded(dir, player);
  }

  render() {
    var p = this.props;
    var st = this.state;
    if (!p.isOpen) return null;

    var match = p.eventActionMatch;
    var game_events = p.game_events || [];
    var team_list = p.team_list || [];
    var leftScoreObj = p.footballMatchLeftTeamScore || {};
    var rightScoreObj = p.footballMatchRightTeamScore || {};

    var t1 = match ? match.team_one : null;
    var t2 = match ? match.team_two : null;
    var s1 = match && match.team1_scores && match.team1_scores[0] != null ? match.team1_scores[0] : 0;
    var s2 = match && match.team2_scores && match.team2_scores[0] != null ? match.team2_scores[0] : 0;
    var isDraw = match && !t1.winner && !t2.winner && Number(s1) === Number(s2);
    var AMBER = "#f59e0b";
    var scoreC1 = isDraw ? AMBER : (t1.winner ? PURPLE : "#111827");
    var scoreC2 = isDraw ? AMBER : (t2.winner ? PURPLE : "#111827");

    var t1Match = t1 ? team_list.filter(function (t) { return t.id == t1.id; })[0] : null;
    var t2Match = t2 ? team_list.filter(function (t) { return t.id == t2.id; })[0] : null;
    var t1Events = match ? (match.match_events || []).filter(function (e) { return e.team && e.team.id == t1.id; }) : [];
    var t2Events = match ? (match.match_events || []).filter(function (e) { return e.team && e.team.id == t2.id; }) : [];

    var leftEvtObj = p.leftOpen ? game_events.find(function (ev) { return String(ev.id) === String(leftScoreObj.event); }) || null : null;
    var rightEvtObj = p.rightOpen ? game_events.find(function (ev) { return String(ev.id) === String(rightScoreObj.event); }) || null : null;

    var colStyle = { flex: 1, minWidth: 0, padding: "0 8px" };
    var labelSt = { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 7, fontFamily: FF };
    var self = this;

    return (
      <div className="hide_in_desktop" style={{ position: "fixed", inset: 0, background: "rgba(15,10,30,0.6)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 9000 }} onClick={p.onClose}>
        <div style={{ background: "#fff", width: "100%", maxHeight: "88vh", borderRadius: "20px 20px 0 0", overflowY: "auto", boxShadow: "0 -12px 40px rgba(0,0,0,0.22)", fontFamily: FF }} onClick={function (e) { e.stopPropagation(); }}>

          {/* Drag handle */}
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
            <div style={{ width: 36, height: 4, borderRadius: 4, background: "#e5e7eb" }} />
          </div>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px 12px" }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: FF }}>Match Scoreboard</span>
            <button type="button" onClick={p.onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: "50%", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
              <XIcon size={12} color="#374151" />
            </button>
          </div>

          {/* Score banner */}
          {match && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "linear-gradient(135deg,#faf5ff,#f0f9ff)", margin: "0 16px 16px", borderRadius: 16, padding: "16px 12px" }}>
              <div style={{ flex: 1, textAlign: "center" }}>
                <TeamAvatar team={t1} size={60} createImageFromInitials={p.createImageFromInitials} getRandomColor={p.getRandomColor} />
                <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginTop: 6, fontFamily: FF }}>{t1.name}</div>
                {t1.winner && <div style={{ fontSize: 10, color: PURPLE, fontWeight: 700, marginTop: 2 }}>Winner ★</div>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 44, fontWeight: 900, color: scoreC1, lineHeight: 1 }}>{s1}</span>
                  <span style={{ fontSize: 20, color: "#9ca3af", fontWeight: 700 }}>–</span>
                  <span style={{ fontSize: 44, fontWeight: 900, color: scoreC2, lineHeight: 1 }}>{s2}</span>
                </div>
                {isDraw && <div style={{ fontSize: 11, fontWeight: 700, color: AMBER, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 20, padding: "2px 10px", fontFamily: FF }}>🤝 Draw</div>}
              </div>
              <div style={{ flex: 1, textAlign: "center" }}>
                <TeamAvatar team={t2} size={60} createImageFromInitials={p.createImageFromInitials} getRandomColor={p.getRandomColor} />
                <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginTop: 6, fontFamily: FF }}>{t2.name}</div>
                {t2.winner && <div style={{ fontSize: 10, color: PURPLE, fontWeight: 700, marginTop: 2 }}>Winner ★</div>}
              </div>
            </div>
          )}

          {/* Save 0–0 */}
          {match && Number(s1) === 0 && Number(s2) === 0 && (
            <div style={{ padding: "0 16px 12px", textAlign: "center" }}>
              <button type="button" onClick={p.onSaveZeroZero} style={{ background: "#f0fdf4", color: "#16a34a", border: "1.5px solid #86efac", borderRadius: 20, padding: "7px 22px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: FF }}>
                Save 0 – 0
              </button>
            </div>
          )}

          {/* Events columns */}
          {match && (
            <div style={{ display: "flex", borderTop: "1px solid #f3f4f6", padding: "4px 8px 20px" }}>
              {/* Team 1 */}
              <div style={colStyle}>
                <div style={{ paddingTop: 12 }}>
                  <div style={labelSt}>{t1 && t1.name}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                    {game_events.map(function (ev, i) {
                      var evMeta = getEventMeta(ev.event);
                      var isActive = p.leftOpen && String(leftScoreObj.event) === String(ev.id);
                      return (
                        <button key={i} type="button" onClick={function () { self.handleEventPill(ev, "left"); }}
                          style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 20, border: "1.5px solid " + (isActive ? evMeta.color : "#e5e7eb"), background: isActive ? evMeta.bg : "#fff", color: isActive ? evMeta.color : "#374151", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: FF }}>
                          <span>{evMeta.emoji}</span> {ev.event}
                        </button>
                      );
                    })}
                  </div>
                  <EventForm
                    dir="left"
                    selectedEvent={leftEvtObj}
                    scoreObj={leftScoreObj}
                    teamMatch={t1Match}
                    extraPlayers={st.leftExtraPlayers}
                    showAddForm={st.leftShowAddForm}
                    onShowAddForm={function () { self.setState({ leftShowAddForm: true }); }}
                    onHideAddForm={function () { self.setState({ leftShowAddForm: false }); }}
                    onPlayerSaved={function (player) { self.handlePlayerSaved("left", player); }}
                    onChangePlayer={p.onChangePlayer}
                    onChangeEventTime={p.onChangeEventTime}
                    onUpdateFootballScore={p.onUpdateFootballScore}
                    onDismiss={null}
                    getLocationOrigin={p.getLocationOrigin}
                    competitionId={p.competitionId}
                  />
                  <div style={{ marginTop: 10 }}>
                    {t1Events.length === 0
                      ? <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: FF, padding: "4px 0" }}>No events yet</div>
                      : t1Events.map(function (info) { return <EventHistoryPill key={info.id} info={info} onDelete={function (id) { p.onDeleteEventHistory(id, "left"); }} />; })}
                  </div>
                </div>
              </div>

              <div style={{ width: 1, background: "#f3f4f6", flexShrink: 0, margin: "12px 0 0" }} />

              {/* Team 2 */}
              <div style={colStyle}>
                <div style={{ paddingTop: 12 }}>
                  <div style={labelSt}>{t2 && t2.name}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                    {game_events.map(function (ev, i) {
                      var evMeta = getEventMeta(ev.event);
                      var isActive = p.rightOpen && String(rightScoreObj.event) === String(ev.id);
                      return (
                        <button key={i} type="button" onClick={function () { self.handleEventPill(ev, "right"); }}
                          style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 8px", borderRadius: 20, border: "1.5px solid " + (isActive ? evMeta.color : "#e5e7eb"), background: isActive ? evMeta.bg : "#fff", color: isActive ? evMeta.color : "#374151", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: FF }}>
                          <span>{evMeta.emoji}</span> {ev.event}
                        </button>
                      );
                    })}
                  </div>
                  <EventForm
                    dir="right"
                    selectedEvent={rightEvtObj}
                    scoreObj={rightScoreObj}
                    teamMatch={t2Match}
                    extraPlayers={st.rightExtraPlayers}
                    showAddForm={st.rightShowAddForm}
                    onShowAddForm={function () { self.setState({ rightShowAddForm: true }); }}
                    onHideAddForm={function () { self.setState({ rightShowAddForm: false }); }}
                    onPlayerSaved={function (player) { self.handlePlayerSaved("right", player); }}
                    onChangePlayer={p.onChangePlayer}
                    onChangeEventTime={p.onChangeEventTime}
                    onUpdateFootballScore={p.onUpdateFootballScore}
                    onDismiss={null}
                    getLocationOrigin={p.getLocationOrigin}
                    competitionId={p.competitionId}
                  />
                  <div style={{ marginTop: 10 }}>
                    {t2Events.length === 0
                      ? <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: FF, padding: "4px 0" }}>No events yet</div>
                      : t2Events.map(function (info) { return <EventHistoryPill key={info.id} info={info} onDelete={function (id) { p.onDeleteEventHistory(id, "right"); }} />; })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ScoreBoardDesktop
// ─────────────────────────────────────────────────────────────────────────────
export class ScoreBoardDesktop extends PureComponent {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    eventActionMatch: PropTypes.object,
    team_list: PropTypes.array,
    game_events: PropTypes.array,
    footballMatchLeftTeamScore: PropTypes.object,
    footballMatchRightTeamScore: PropTypes.object,
    selectedEvent: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    leftOpen: PropTypes.bool,
    rightOpen: PropTypes.bool,
    knockout_matches_created: PropTypes.bool,
    submit_button_pressed: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onHandleEvent: PropTypes.func.isRequired,
    onToggleOpen: PropTypes.func,
    onChangePlayer: PropTypes.func.isRequired,
    onChangeEventTime: PropTypes.func.isRequired,
    onUpdateFootballScore: PropTypes.func.isRequired,
    onDeleteEventHistory: PropTypes.func.isRequired,
    onSaveZeroZero: PropTypes.func.isRequired,
    createImageFromInitials: PropTypes.func.isRequired,
    getRandomColor: PropTypes.func.isRequired,
    getLocationOrigin: PropTypes.func,
    competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onPlayerAdded: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      leftExtraPlayers: [],
      rightExtraPlayers: [],
      leftShowAddForm: false,
      rightShowAddForm: false,
    };
    this.handleEventPill = this.handleEventPill.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handlePlayerSaved = this.handlePlayerSaved.bind(this);
  }

  handleEventPill(event, dir) {
    var leftOpen = this.props.leftOpen;
    var rightOpen = this.props.rightOpen;
    if (dir === "left" && !leftOpen && this.props.onToggleOpen) this.props.onToggleOpen("left", leftOpen, rightOpen);
    if (dir === "right" && !rightOpen && this.props.onToggleOpen) this.props.onToggleOpen("right", leftOpen, rightOpen);
    this.props.onHandleEvent(event, dir);
  }

  handleDismiss(dir) {
    if (this.props.onToggleOpen) {
      this.props.onToggleOpen(dir, this.props.leftOpen, this.props.rightOpen);
    }
  }

  handlePlayerSaved(dir, player) {
    var extraKey = dir === "left" ? "leftExtraPlayers" : "rightExtraPlayers";
    var formKey = dir === "left" ? "leftShowAddForm" : "rightShowAddForm";
    this.setState(function (prev) {
      var update = {};
      update[extraKey] = prev[extraKey].concat(player);
      update[formKey] = false;
      return update;
    });
    // Auto-select the newly added player in the dropdown
    this.props.onChangePlayer({ target: { value: String(player.id) } }, dir);
    if (this.props.onPlayerAdded) this.props.onPlayerAdded(dir, player);
  }

  render() {
    var p = this.props;
    var st = this.state;
    if (!p.isOpen) return null;

    var match = p.eventActionMatch;
    var game_events = p.game_events || [];
    var team_list = p.team_list || [];
    var leftScoreObj = p.footballMatchLeftTeamScore || {};
    var rightScoreObj = p.footballMatchRightTeamScore || {};
    var selEvt = (p.selectedEvent && typeof p.selectedEvent === "object") ? p.selectedEvent : null;

    var t1 = match ? match.team_one : null;
    var t2 = match ? match.team_two : null;
    var s1 = match && match.team1_scores && match.team1_scores[0] != null ? match.team1_scores[0] : 0;
    var s2 = match && match.team2_scores && match.team2_scores[0] != null ? match.team2_scores[0] : 0;
    var isDraw = match && !t1.winner && !t2.winner && Number(s1) === Number(s2);
    var AMBER = "#f59e0b";
    var scoreC1 = isDraw ? AMBER : (t1.winner ? PURPLE : "#111827");
    var scoreC2 = isDraw ? AMBER : (t2.winner ? PURPLE : "#111827");

    var t1Match = t1 ? team_list.filter(function (t) { return t.id == t1.id; })[0] : null;
    var t2Match = t2 ? team_list.filter(function (t) { return t.id == t2.id; })[0] : null;
    var t1Events = match ? (match.match_events || []).filter(function (e) { return e.team && e.team.id == t1.id; }) : [];
    var t2Events = match ? (match.match_events || []).filter(function (e) { return e.team && e.team.id == t2.id; }) : [];

    var leftFormEvt = p.leftOpen ? selEvt : null;
    var rightFormEvt = p.rightOpen ? selEvt : null;

    var self = this;
    var colStyle = { flex: 1, minWidth: 0, padding: "20px 22px 20px" };
    var labelSt = { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 9, fontFamily: FF };

    return (
      <div className="hide_in_mobile" style={{ position: "fixed", inset: 0, background: "rgba(15,10,30,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: 20 }} onClick={p.onClose}>
        <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 780, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.24)", fontFamily: FF, position: "relative" }} onClick={function (e) { e.stopPropagation(); }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #f3f4f6", position: "sticky", top: 0, background: "#fff", zIndex: 5, borderRadius: "20px 20px 0 0" }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: FF }}>Match Scoreboard</span>
            <button type="button" onClick={p.onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
              <XIcon size={13} color="#374151" />
            </button>
          </div>

          {/* Score banner */}
          {match && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "linear-gradient(135deg,#faf5ff,#f0f9ff)", borderBottom: "1px solid #f3f4f6", padding: "24px 32px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flex: 1 }}>
                <TeamAvatar team={t1} size={80} createImageFromInitials={p.createImageFromInitials} getRandomColor={p.getRandomColor} />
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", textAlign: "center", fontFamily: FF }}>
                  {t1.name}
                  {t1.winner && <div style={{ fontSize: 10, color: PURPLE, fontWeight: 700, marginTop: 2 }}>Winner ★</div>}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span style={{ fontSize: 68, fontWeight: 900, color: scoreC1, lineHeight: 1 }}>{s1}</span>
                  <span style={{ fontSize: 28, color: "#9ca3af", fontWeight: 700 }}>–</span>
                  <span style={{ fontSize: 68, fontWeight: 900, color: scoreC2, lineHeight: 1 }}>{s2}</span>
                </div>
                {isDraw && <div style={{ fontSize: 12, fontWeight: 700, color: AMBER, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 20, padding: "3px 14px", fontFamily: FF }}>🤝 Draw</div>}
                {Number(s1) === 0 && Number(s2) === 0 && (
                  <button type="button" onClick={p.onSaveZeroZero} style={{ marginTop: 8, background: "#f0fdf4", color: "#16a34a", border: "1.5px solid #86efac", borderRadius: 20, padding: "7px 26px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: FF }}>
                    ✔ Save 0 – 0
                  </button>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flex: 1 }}>
                <TeamAvatar team={t2} size={80} createImageFromInitials={p.createImageFromInitials} getRandomColor={p.getRandomColor} />
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", textAlign: "center", fontFamily: FF }}>
                  {t2.name}
                  {t2.winner && <div style={{ fontSize: 10, color: PURPLE, fontWeight: 700, marginTop: 2 }}>Winner ★</div>}
                </div>
              </div>
            </div>
          )}

          {/* Events section */}
          {match && (
            <div style={{ display: "flex", alignItems: "stretch" }}>
              {/* Team 1 column */}
              <div style={{ ...colStyle, borderRight: "1px solid #f3f4f6" }}>
                <div style={labelSt}>{t1 && t1.name}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
                  {game_events.map(function (ev, i) {
                    var evMeta = getEventMeta(ev.event);
                    var isActive = p.leftOpen && selEvt && selEvt.id === ev.id;
                    return (
                      <button key={i} type="button" onClick={function () { self.handleEventPill(ev, "left"); }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 20, border: "1.5px solid " + (isActive ? evMeta.color : "#e5e7eb"), background: isActive ? evMeta.bg : "#fff", color: isActive ? evMeta.color : "#374151", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FF, transition: "all 0.14s" }}>
                        <span>{evMeta.emoji}</span> {ev.event}
                      </button>
                    );
                  })}
                </div>
                <EventForm
                  dir="left"
                  selectedEvent={leftFormEvt}
                  scoreObj={leftScoreObj}
                  teamMatch={t1Match}
                  extraPlayers={st.leftExtraPlayers}
                  showAddForm={st.leftShowAddForm}
                  onShowAddForm={function () { self.setState({ leftShowAddForm: true }); }}
                  onHideAddForm={function () { self.setState({ leftShowAddForm: false }); }}
                  onPlayerSaved={function (player) { self.handlePlayerSaved("left", player); }}
                  onChangePlayer={p.onChangePlayer}
                  onChangeEventTime={p.onChangeEventTime}
                  onUpdateFootballScore={p.onUpdateFootballScore}
                  onDismiss={function () { self.handleDismiss("left"); }}
                  getLocationOrigin={p.getLocationOrigin}
                  competitionId={p.competitionId}
                />
                <div style={{ marginTop: 12 }}>
                  {t1Events.length === 0
                    ? <div style={{ fontSize: 12, color: "#9ca3af", fontFamily: FF }}>No events logged yet</div>
                    : t1Events.map(function (info) { return <EventHistoryPill key={info.id} info={info} onDelete={function (id) { p.onDeleteEventHistory(id, "left"); }} />; })}
                </div>
              </div>

              {/* Team 2 column */}
              <div style={colStyle}>
                <div style={labelSt}>{t2 && t2.name}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
                  {game_events.map(function (ev, i) {
                    var evMeta = getEventMeta(ev.event);
                    var isActive = p.rightOpen && selEvt && selEvt.id === ev.id;
                    return (
                      <button key={i} type="button" onClick={function () { self.handleEventPill(ev, "right"); }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 20, border: "1.5px solid " + (isActive ? evMeta.color : "#e5e7eb"), background: isActive ? evMeta.bg : "#fff", color: isActive ? evMeta.color : "#374151", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FF, transition: "all 0.14s" }}>
                        <span>{evMeta.emoji}</span> {ev.event}
                      </button>
                    );
                  })}
                </div>
                <EventForm
                  dir="right"
                  selectedEvent={rightFormEvt}
                  scoreObj={rightScoreObj}
                  teamMatch={t2Match}
                  extraPlayers={st.rightExtraPlayers}
                  showAddForm={st.rightShowAddForm}
                  onShowAddForm={function () { self.setState({ rightShowAddForm: true }); }}
                  onHideAddForm={function () { self.setState({ rightShowAddForm: false }); }}
                  onPlayerSaved={function (player) { self.handlePlayerSaved("right", player); }}
                  onChangePlayer={p.onChangePlayer}
                  onChangeEventTime={p.onChangeEventTime}
                  onUpdateFootballScore={p.onUpdateFootballScore}
                  onDismiss={function () { self.handleDismiss("right"); }}
                  getLocationOrigin={p.getLocationOrigin}
                  competitionId={p.competitionId}
                />
                <div style={{ marginTop: 12 }}>
                  {t2Events.length === 0
                    ? <div style={{ fontSize: 12, color: "#9ca3af", fontFamily: FF }}>No events logged yet</div>
                    : t2Events.map(function (info) { return <EventHistoryPill key={info.id} info={info} onDelete={function (id) { p.onDeleteEventHistory(id, "right"); }} />; })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/app/views/partner/crud/viewFixtureScreen/ScoreboardDialogs.js